"use client";

import {
	Button,
	type DefaultLinkProps,
	Image,
	Link,
	Price,
	Rating
} from "@pamperedchef/design-system";
import { useAddToCartSlider } from "@/app/hooks/useAddToCartSlider";
import type { UpsellSetFeaturedProduct } from "./UpsellSet";
import * as styles from "./UpsellSet.module.scss";

export interface UpsellSetCardProps {
	featured: UpsellSetFeaturedProduct;
	addToCartLabel?: string;
	/** Override for stories and tests; production uses the shared hook. */
	onAddToCart?: (sku: string) => void;
}

/**
 * Its own component so `useAddToCartSlider` is called unconditionally — the
 * banner renders the card only when the set resolves.
 */
export function UpsellSetCard({
	featured,
	addToCartLabel = "Add to Cart",
	onAddToCart
}: UpsellSetCardProps) {
	const salePrice = featured.price?.display ?? 0;

	const { onClick } = useAddToCartSlider({
		sku: featured.sku,
		quantity: 1,
		product: {
			sku: featured.sku,
			quantity: 1,
			name: featured.name,
			imageUrl: featured.image.src,
			listPrice: featured.price?.strikeThrough ?? salePrice,
			salePrice
		}
	});

	return (
		<article className={styles["upsell__card"]}>
			<a
				href={featured.href}
				aria-hidden="true"
				tabIndex={-1}
				className={styles["upsell__card-image-link"]}
			>
				<Image
					src={featured.image.src}
					alt={featured.image.alt ?? ""}
					aspectRatio="1:1"
					className={styles["upsell__card-image"]}
				/>
			</a>

			<div className={styles["upsell__card-content"]}>
				<Link<DefaultLinkProps>
					href={featured.href}
					size="lg"
					className={styles["upsell__card-name"]}
				>
					{featured.name}
				</Link>

				{featured.rating ? (
					<Rating
						{...featured.rating}
						size="sm"
						className={styles["upsell__card-rating"]}
					/>
				) : null}

				{featured.price ? (
					<Price
						{...featured.price}
						size="medium"
						theme="dark"
						className={styles["upsell__card-price"]}
					/>
				) : null}

				{!featured.isVirtualProduct ? (
					<Button
						kind="secondary"
						size="sm"
						className={styles["upsell__card-cta"]}
						onPress={
							onAddToCart
								? () => onAddToCart(featured.sku)
								: onClick
						}
					>
						{addToCartLabel}
					</Button>
				) : null}
			</div>
		</article>
	);
}
